import { motion } from "framer-motion";
import { ArrowRight, Heart, Scissors, Star, Package } from "lucide-react";
import { Link } from "react-router";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { fadeUp, stagger } from "@/lib/animations";
import Blob from "@/components/ui/Blob";
import StitchDots from "@/components/ui/StitchDots";

interface Chip {
  icon: React.ReactNode;
  label: string;
  pos: { top?: string; bottom?: string; left?: string; right?: string };
  rot: number;
  delay: number;
}

export default function HeroVariantC() {
  const theme = usePalette();

  const chips: Chip[] = [
    {
      icon: (
        <Heart size={16} style={{ color: colors.berryPink }} fill={colors.berryPink} />
      ),
      label: "150K Fam",
      pos: { top: "20%", left: "8%" },
      rot: -4,
      delay: 1.0,
    },
    {
      icon: <Scissors size={16} style={{ color: theme.accent }} />,
      label: "Creating Daily",
      pos: { top: "28%", right: "10%" },
      rot: 3,
      delay: 1.15,
    },
    {
      icon: <Star size={16} style={{ color: theme.warm }} fill={theme.warm} />,
      label: "Patterns Shipping",
      pos: { bottom: "30%", left: "12%" },
      rot: 2,
      delay: 1.3,
    },
    {
      icon: <Package size={16} style={{ color: colors.forestGreen }} />,
      label: "Retreats 2026",
      pos: { bottom: "22%", right: "14%" },
      rot: -3,
      delay: 1.45,
    },
  ];

  return (
    <section
      className="relative min-h-dvh flex items-center overflow-hidden pt-20"
      style={{ backgroundColor: colors.cream }}
    >
      <Blob className="w-96 h-96 top-10 -left-20" color={theme.blob1} />
      <Blob className="w-80 h-80 bottom-10 right-0" color={theme.blob2} />
      <Blob className="w-72 h-72 top-1/4 right-1/4" color={theme.blob3} />
      <StitchDots />

      {/* Constellation chips */}
      {chips.map((chip, i) => (
        <motion.div
          key={i}
          className="absolute z-20 rounded-2xl shadow-lg px-3.5 py-2.5 flex items-center gap-2 hidden md:flex"
          style={{
            backgroundColor: colors.cream,
            transform: `rotate(${chip.rot}deg)`,
            ...chip.pos,
            border: `1px solid ${colors.tan}20`,
          }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{
            opacity: 1,
            scale: 1,
            y: [0, i % 2 === 0 ? -5 : 5, 0],
          }}
          transition={{
            opacity: { duration: 0.6, delay: chip.delay },
            scale: { duration: 0.6, delay: chip.delay },
            y: {
              repeat: Infinity,
              duration: 3 + i * 0.4,
              ease: "easeInOut",
              delay: chip.delay,
            },
          }}
        >
          {chip.icon}
          <span
            className="text-xs"
            style={{
              fontFamily: fonts.body,
              fontWeight: 700,
              color: colors.navy,
            }}
          >
            {chip.label}
          </span>
        </motion.div>
      ))}

      {/* Caveat "join me" overlay */}
      <motion.div
        className="absolute z-20 hidden lg:flex flex-col items-center"
        style={{ top: "58%", right: "22%" }}
        initial={{ opacity: 0, rotate: -20 }}
        animate={{ opacity: 1, rotate: -8 }}
        transition={{ duration: 0.7, delay: 1.6 }}
      >
        <span
          className="text-3xl"
          style={{
            fontFamily: fonts.heading,
            color: theme.warm,
          }}
        >
          join me!
        </span>
        <motion.svg
          width="60"
          height="40"
          viewBox="0 0 60 40"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1, delay: 2 }}
        >
          <motion.path
            d="M50 5 Q30 15, 10 30 L18 28 M10 30 L14 22"
            stroke={theme.warm}
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="4 3"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1, delay: 2 }}
          />
        </motion.svg>
      </motion.div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 w-full text-center">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.h1
            variants={fadeUp}
            custom={0}
            className="text-5xl sm:text-6xl lg:text-7xl leading-[1.08] mb-6"
            style={{
              fontFamily: fonts.logo,
              fontWeight: 400,
              color: colors.navy,
            }}
          >
            Loose threads,{" "}
            <span
              className="relative inline-block"
              style={{ color: theme.accent }}
            >
              big ideas
              <motion.svg
                className="absolute -bottom-1 left-0 w-full"
                viewBox="0 0 200 12"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1, delay: 0.8 }}
              >
                <motion.path
                  d="M2 8 Q50 2, 100 7 T198 5"
                  stroke={theme.warm}
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray="6 4"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.8 }}
                />
              </motion.svg>
            </span>
            <span style={{ color: theme.warm }}>!</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            custom={1}
            className="text-base sm:text-lg mb-8 max-w-md mx-auto"
            style={{
              fontFamily: fonts.body,
              fontWeight: 500,
              color: colors.navy,
              opacity: 0.75,
              letterSpacing: "0.03em",
            }}
          >
            A weekend of sewing, creativity, and connection.
          </motion.p>

          <motion.div
            variants={fadeUp}
            custom={2}
            className="flex flex-col sm:flex-row items-center gap-3 justify-center"
          >
            <Link
              to="/retreats"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-white cursor-pointer transition-all duration-250 hover:shadow-xl hover:scale-105 active:scale-[0.98]"
              style={{
                fontFamily: fonts.button,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase" as const,
                fontSize: "0.75rem",
                backgroundColor: theme.accent,
              }}
            >
              Join a Retreat
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full cursor-pointer transition-all duration-250 hover:shadow-lg hover:scale-105 active:scale-[0.98]"
              style={{
                fontFamily: fonts.button,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase" as const,
                fontSize: "0.75rem",
                color: colors.navy,
                backgroundColor: colors.cream,
                border: `1px solid ${colors.tan}55`,
              }}
            >
              Shop Patterns
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
